import { useEffect, useState } from 'react'
import * as XLSX from 'xlsx'
import { pdfFitWidth } from '../pdfView'
import { fill, PAGES_FILE_VIEWER_TEXTS as T } from '../../shared/texts'

// In-app document viewer tab. PDFs render inline in an iframe (browser viewer);
// spreadsheets (xlsx/xls/csv/tsv) are fetched and rendered as a table via SheetJS,
// which the browser cannot display natively. Opened from the Monats-Export
// explorer / report action.

const SHEET_EXT = new Set(['csv', 'tsv', 'xls', 'xlsx', 'xlsm'])

function extOf(name = ''): string {
    const m = name.toLowerCase().match(/\.([a-z0-9]+)(?:\?|$)/)
    return m ? m[1] : ''
}

export default function FileViewer({ url, fname }: { url?: string, fname?: string }) {
    if (!url) return <div className="muted" style={{ padding: 24 }}>{T.noFile}</div>
    if (SHEET_EXT.has(extOf(fname || url))) return <SheetViewer url={url} />
    return (
        <div className="doc-viewer">
            {/* Seitenbreite: im Dokument selbst gesetzt, hier als Netz fuer aeltere
                Dateien ohne die Anweisung — siehe pdfView.ts. */}
            <iframe src={pdfFitWidth(url)} title={T.documentTitle} />
        </div>
    )
}

interface Sheet { name: string, rows: (string | number)[][] }

function SheetViewer({ url }: { url: string }) {
    const [sheets, setSheets] = useState<Sheet[] | null>(null)
    const [active, setActive] = useState(0)
    const [error, setError] = useState('')

    useEffect(() => {
        let alive = true
        fetch(url, { credentials: 'include' })
            .then((r) => { if (!r.ok) throw new Error(fill(T.httpError, { status: r.status })); return r.arrayBuffer() })
            .then((buf) => {
                const wb = XLSX.read(buf, { type: 'array' })
                const list: Sheet[] = wb.SheetNames.map((name) => ({
                    name,
                    rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false, defval: '' }) as (string | number)[][],
                }))
                if (alive) setSheets(list)
            })
            .catch((e) => { if (alive) setError(e?.message ?? T.loadError) })
        return () => { alive = false }
    }, [url])

    if (error) return <div className="notice err" style={{ margin: 24 }}>{error}</div>
    if (!sheets) return <p className="muted" style={{ padding: 24 }}>{T.loading}</p>
    if (sheets.length === 0) return <p className="muted" style={{ padding: 24 }}>{T.emptyFile}</p>

    const rows = sheets[active]?.rows ?? []
    const [head, ...body] = rows

    return (
        <div className="sheet-viewer">
            {sheets.length > 1 && (
                <div className="sheet-tabs">
                    {sheets.map((s, i) => (
                        <button key={s.name} type="button"
                            className={`sheet-tab${i === active ? ' active' : ''}`}
                            onClick={() => setActive(i)}>{s.name}</button>
                    ))}
                </div>
            )}
            <div className="sheet-scroll">
                <table className="sheet-table">
                    {head && (
                        <thead><tr>{head.map((c, i) => <th key={i}>{String(c)}</th>)}</tr></thead>
                    )}
                    <tbody>
                        {body.map((r, ri) => (
                            <tr key={ri}>{(head ?? r).map((_, ci) => <td key={ci}>{String(r[ci] ?? '')}</td>)}</tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
